import { useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '../lib/api'
import { authClient } from '../lib/authClient'
import type { Pwa } from '../lib/pwa'
import { Thumb } from './Thumb'
import { allergenName, asNewAllergens, dietExcludes, listNames, unansweredAllergens, type Category, type DraftAllergen, type KnownAllergen, type NewFamily, type ReminderSettings, type Session, type StarterItem } from '@shared/types'
import { AllergenPicker } from './AllergenPicker'
import { InstallPrompt } from './InstallPrompt'

/**
 * A signed-in person with no family yet. Five short steps, then the board.
 *
 * Nothing is saved until the last step: the family, its child, the pantry
 * and the reminder land in one request, so closing the tab halfway leaves an
 * account and nothing else -- which is exactly what "sign up, get distracted,
 * come back tomorrow" should leave.
 */

type Step = 'family' | 'partner' | 'allergens' | 'pantry' | 'reminder' | 'done'
/* Allergens come before the pantry so the starter list can warn about itself. */
const ORDER: Step[] = ['family', 'partner', 'allergens', 'pantry', 'reminder']

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Australia/Sydney',
]
const hourLabel = (h: number) => (h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`)

/** The device's zone, if it says; a US default otherwise. */
function detectedZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  } catch {
    return 'America/New_York'
  }
}

/** One child while onboarding is still collecting them. */
interface Kid {
  key: string
  name: string
  allergens: DraftAllergen[]
}

/** A pantry row as the review step holds it: the starter food, or one typed in. */
interface Row {
  key: string
  /** The starter row it came from; null for one typed in here. */
  starterId: string | null
  categoryId: string
  name: string
  emoji: string
  kept: boolean
  /** The brands a generic food goes by, or how it travels. */
  notes: string
  /** What the starter list says this food usually contains. */
  allergens: KnownAllergen[]
}

interface Props {
  session: Session
  pwa: Pwa
  /**
   * Walk the steps without writing anything, for an admin checking what a new
   * family sees. Reached with ?preview=onboarding.
   */
  preview?: boolean
  onDone: (session: Session) => void
  onSignedOut: () => void
}

export function Onboarding({ session, pwa, preview = false, onDone, onSignedOut }: Props) {
  const [step, setStep] = useState<Step>('family')
  const [familyName, setFamilyName] = useState('')
  /*
   * The children, eldest first, each carrying their own allergens. Always at
   * least one row so the first field is there to type in.
   *
   * Allergens live on the child rather than in one list beside them because
   * they are the one thing that must not be shared by accident: the dad of
   * four who asked for this has an oldest allowed peanut butter at school and
   * two middle ones who are not.
   */
  const [kids, setKids] = useState<Kid[]>([{ key: 'k0', name: '', allergens: [] }])
  /** Which child's allergens the tabs on that step are showing. */
  const [whichKid, setWhichKid] = useState(0)
  const [partnerName, setPartnerName] = useState('')
  const [partnerEmail, setPartnerEmail] = useState('')
  const [categories, setCategories] = useState<Category[]>([])
  const [rows, setRows] = useState<Row[]>([])
  const [adding, setAdding] = useState<Record<string, string>>({})
  const [pasting, setPasting] = useState(false)
  const [paste, setPaste] = useState('')
  /** Pasted lines that matched nothing, waiting to be filed. */
  const [review, setReview] = useState<{ key: string; name: string; categoryId: string }[]>([])
  const [pasted, setPasted] = useState<{ added: number; matched: number } | null>(null)
  const [showingInstall, setShowingInstall] = useState(false)
  const [reminder, setReminder] = useState<ReminderSettings>(() => ({
    enabled: true,
    weekday: 0,
    // Sunday evening, where the planning actually happens. See settings.ts.
    hour: 17,
    timezone: detectedZone(),
  }))
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ session: Session; invited: boolean } | null>(null)

  useEffect(() => {
    api
      .starterPantry()
      .then((r) => {
        setCategories(r.categories)
        setRows(
          r.items.map((it: StarterItem) => ({
            key: it.id,
            starterId: it.id,
            categoryId: it.categoryId,
            name: it.name,
            emoji: it.emoji,
            kept: true,
            notes: it.notes ?? '',
            allergens: it.allergens ?? [],
          })),
        )
      })
      .catch(() => setError('Could not load the starter pantry. Check your connection and reload.'))
  }, [])

  const zones = useMemo(
    () => (ZONES.includes(reminder.timezone) ? ZONES : [reminder.timezone, ...ZONES]),
    [reminder.timezone],
  )
  const kept = rows.filter((r) => r.kept).length

  /*
   * Which starter tags the answers just given rule out. Free-text allergens
   * are left out: they cannot match a slug, and guessing at wording is how an
   * app ends up confidently wrong about a child's food.
   */
  const bans = useMemo(() => {
    const severe = new Set<string>()
    const rule = new Set<string>()
    const avoid = new Set<string>()
    /*
     * Every child's, pooled. The pantry belongs to the family, so a food that
     * one child cannot have is a food to untick -- erring towards leaving it
     * out, which a parent can undo in one tap, rather than towards offering
     * it, which they might not notice.
     */
    for (const a of kids.flatMap((k) => k.allergens)) {
      if (a.slug === 'other') continue
      /*
       * A diet has to be expanded before it can match anything. No food is
       * tagged "vegetarian"; the meat ones are tagged meat, and this is what
       * turns one tick into a flag on every starter food carrying any of the
       * ingredients it rules out.
       */
      if (a.kind === 'diet') for (const slug of dietExcludes(a.slug)) rule.add(slug)
      else if (a.kind === 'school') rule.add(a.slug)
      else if (a.severity === 'severe') severe.add(a.slug)
      else avoid.add(a.slug)
    }
    return { severe, rule, avoid }
  }, [kids])

  /* The children who have actually been named, and how to say them in a line. */
  const named = kids.filter((k) => k.name.trim())
  const kidNames = named.map((k) => k.name.trim())
  const theirs = kidNames.length > 0 ? listNames(kidNames) : ''
  /** "Ada" or "they", for copy that reads either way. */
  const someone = kidNames.length === 1 ? kidNames[0]! : 'they'
  const possessive = kidNames.length === 1 ? `${kidNames[0]!}’s` : 'Your family’s'

  const hardHits = (r: Row) => r.allergens.filter((s) => bans.severe.has(s) || bans.rule.has(s))
  const softHits = (r: Row) => r.allergens.filter((s) => bans.avoid.has(s))
  const droppedCount = rows.filter((r) => !r.kept && hardHits(r).length > 0).length
  const at = ORDER.indexOf(step)

  function addFood(categoryId: string) {
    const name = (adding[categoryId] ?? '').trim()
    if (!name) return
    const dup = rows.some((r) => r.categoryId === categoryId && r.name.toLowerCase() === name.toLowerCase())
    if (!dup) {
      setRows((rs) => [...rs, { key: `u${Date.now()}`, starterId: null, categoryId, name, emoji: '', kept: true, notes: '', allergens: [] }])
    } else {
      setRows((rs) =>
        rs.map((r) => (r.categoryId === categoryId && r.name.toLowerCase() === name.toLowerCase() ? { ...r, kept: true } : r)),
      )
    }
    setAdding((a) => ({ ...a, [categoryId]: '' }))
  }

  /*
   * A pasted list, matched against what is already on the screen.
   *
   * Deliberately not the app's bulk add: that writes through the API, and
   * during onboarding there is no family to write to yet. It also cannot sort
   * with AI here, because every family is pending until someone approves it.
   *
   * Matching runs across every category, not just one. The starter list is
   * ninety-odd foods covering the common ground, so most pasted lines are
   * already down there: "Blueberries" should tick the Blueberries in Fruit,
   * not make a second one in whatever category a dropdown happened to say.
   * What is left over is genuinely new, and short enough to file one at a
   * time on the review rows below.
   */
  function addPasted() {
    const names = [
      ...new Set(
        paste
          .split(/[\n,]/)
          .map((line) => line.replace(/^[\s\-*\u2022\d.)]+/, '').trim().slice(0, 60))
          .filter(Boolean),
      ),
    ].slice(0, 60)
    if (names.length === 0) return

    const known = new Map(rows.map((r) => [r.name.toLowerCase(), r]))
    const matched = names.filter((n) => known.has(n.toLowerCase()))
    const fresh = names.filter((n) => !known.has(n.toLowerCase()))
    const back = new Set(matched.map((n) => n.toLowerCase()))

    setRows((rs) => rs.map((r) => (back.has(r.name.toLowerCase()) ? { ...r, kept: true } : r)))
    setReview(
      fresh.map((name, i) => ({
        key: `p${Date.now()}_${i}`,
        name,
        categoryId: guessCategory(name) ?? categories[0]?.id ?? '',
      })),
    )
    setPasted({ added: fresh.length, matched: matched.length })
    setPaste('')
  }

  /*
   * A guess at where a new food belongs, from the category of the starter
   * food whose name it contains or is contained by. "Ham sandwich" finds
   * Turkey Sandwich and lands in Main Dish. Wrong sometimes, and every row
   * is a dropdown, so wrong is one tap to fix.
   */
  function guessCategory(name: string): string | null {
    const n = name.toLowerCase()
    for (const r of rows) {
      const other = r.name.toLowerCase()
      if (n.includes(other) || other.includes(n)) return r.categoryId
    }
    const word = n.split(/\s+/).pop() ?? ''
    if (word.length >= 4) {
      const hit = rows.find((r) => r.name.toLowerCase().split(/\s+/).includes(word))
      if (hit) return hit.categoryId
    }
    return null
  }

  /** File the leftovers: they land in the pantry when the rows are accepted. */
  function keepReviewed() {
    setRows((rs) => [
      ...rs,
      ...review
        .filter((r) => r.name.trim() && r.categoryId)
        .map((r) => ({
          key: r.key,
          starterId: null,
          categoryId: r.categoryId,
          name: r.name.trim(),
          emoji: '',
          kept: true,
          notes: '',
          allergens: [] as KnownAllergen[],
        })),
    ])
    setReview([])
    setPasted(null)
  }

  function next() {
    setError(null)
    if (step === 'family') {
      if (!familyName.trim()) return setError('Give your family a name — “The Lees” works.')
      if (named.length === 0) return setError('Whose lunches are these? A first name is enough.')
      const clash = kidNames.find((n, i) => kidNames.findIndex((m) => m.toLowerCase() === n.toLowerCase()) !== i)
      if (clash) return setError(`You have two children called ${clash}. Give them different names.`)
      // Blank rows are how the repeater looks while it is being used; only
      // the named ones go on, and the tabs below index into that list.
      setKids(named)
      setWhichKid(0)
    }
    if (step === 'partner') {
      const n = partnerName.trim()
      const e = partnerEmail.trim()
      if ((n && !e) || (!n && e)) return setError('A name and an email, or neither.')
      if (e && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return setError('That email does not look right.')
      if (e && e.toLowerCase() === session.email.toLowerCase()) {
        return setError('That is your own email. Your partner needs their own.')
      }
    }
    if (step === 'allergens') {
      // Every ticked allergen has to say how serious it is. There is no safe
      // default, and a wrong guess here is the one that matters most.
      /*
       * Across every child, not just the one on screen. A severity left
       * unanswered on a tab nobody went back to is exactly the one that would
       * slip through, so the error names the child as well as the allergen.
       */
      for (const [i, kid] of named.entries()) {
        const waiting = unansweredAllergens(kid.allergens)
        if (waiting.length === 0) continue
        setWhichKid(i)
        return setError(
          waiting.length === 1
            ? `Say whether ${allergenName(waiting[0]!).toLowerCase()} is severe or just one to avoid for ${kid.name.trim()}.`
            : `${kid.name.trim()} has ${waiting.length} allergies still to answer: severe, or just one to avoid?`,
        )
      }
      // Anything hitting a severe allergy or a school rule starts unticked.
      // A milder "avoid" stays: that is a judgement call, not a rule.
      setRows((rs) => rs.map((r) => (hardHits(r).length > 0 ? { ...r, kept: false } : r)))
    }
    if (step === 'pantry' && kept === 0) return setError('Keep at least one food, or the board has nothing to offer.')
    setStep(ORDER[at + 1] ?? 'reminder')
  }

  async function create() {
    if (busy) return
    if (preview) {
      // Nothing is written. The last screen is part of what an admin came to see.
      setResult({ session, invited: Boolean(partnerName.trim()) })
      setStep('done')
      return
    }
    setBusy(true)
    setError(null)
    const input: NewFamily = {
      familyName: familyName.trim(),
      children: named.map((k) => ({ name: k.name.trim(), allergens: asNewAllergens(k.allergens) })),
      partner: partnerName.trim() ? { name: partnerName.trim(), email: partnerEmail.trim() } : null,
      items: rows.filter((r) => r.kept).map(({ categoryId, name, emoji, starterId }) => ({ categoryId, name, emoji, starterId })),
      reminder,
    }
    try {
      const r = await api.createFamily(input)
      setResult(r)
      setStep('done')
      window.history.replaceState(null, '', '/')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the family. Try again.')
    } finally {
      setBusy(false)
    }
  }

  async function signOut() {
    try {
      await authClient.signOut()
    } finally {
      onSignedOut()
    }
  }

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('standalone' in navigator && (navigator as { standalone?: boolean }).standalone)

  return (
    <div className="lock onboarding">
      <div className="lock-card wide">
        {preview && (
          <div className="banner info">
            <b>Preview.</b> This is what a new family walks through. Nothing here is saved, no
            emails go out, and your own family is untouched.{' '}
            <button type="button" className="linklike" onClick={() => (window.location.href = '/')}>
              Leave the preview
            </button>
          </div>
        )}
        <div className="onboarding-head">
          <span className="lock-logo" aria-hidden="true">
            🥪
          </span>
          {step !== 'done' && (
            <ol className="steps" aria-label="Progress">
              {ORDER.map((s, i) => (
                <li key={s} aria-current={s === step ? 'step' : undefined} className={i < at ? 'past' : ''} />
              ))}
            </ol>
          )}
        </div>

        {error && <div className="banner error">{error}</div>}

        {step === 'family' && (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault()
              next()
            }}
          >
            <div>
              <h1>Hi {session.name}. Your family?</h1>
              <p className="sub">Both parents see the same board, so it belongs to the family, not to you.</p>
            </div>
            <div className="field">
              <label htmlFor="ob-family">Family name</label>
              <input
                id="ob-family"
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="The Lees"
                maxLength={60}
                autoFocus
              />
            </div>
            <div className="field">
              <label htmlFor="ob-child">Whose lunches? (first names)</label>
              <div className="kid-rows">
                {kids.map((k, i) => (
                  <div className="kid-row" key={k.key}>
                    <input
                      id={i === 0 ? 'ob-child' : `ob-child-${i}`}
                      type="text"
                      value={k.name}
                      onChange={(e) =>
                        setKids((ks) => ks.map((x) => (x.key === k.key ? { ...x, name: e.target.value } : x)))
                      }
                      placeholder={i === 0 ? 'Kit' : 'And their sibling'}
                      maxLength={40}
                      /* The first row is what the field's own label names, so
                         giving it an aria-label as well would replace "Whose
                         lunches?" with something less useful. Later rows have
                         no label of their own and need one. */
                      aria-label={i === 0 ? undefined : `Child ${i + 1}’s name`}
                    />
                    {/* Never on the first row: a family has at least one child,
                        and a remove that empties the list is a dead end. */}
                    {kids.length > 1 && (
                      <button
                        className="btn ghost icon"
                        type="button"
                        aria-label={k.name.trim() ? `Remove ${k.name.trim()}` : `Remove child ${i + 1}`}
                        onClick={() => setKids((ks) => ks.filter((x) => x.key !== k.key))}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {kids.length < 12 && (
                <button
                  className="btn ghost sm"
                  type="button"
                  onClick={() => setKids((ks) => [...ks, { key: `k${Date.now()}`, name: '', allergens: [] }])}
                >
                  + Add another child
                </button>
              )}
              <span className="hint">
                Each child gets their own board and their own allergies. The pantry is the family’s.
              </span>
            </div>
            <div className="row">
              {/* Sign out belongs to the real flow, where someone with no
                  family may want to leave. In a preview it is a live button
                  that signs an admin out of their own account, which is not
                  what "let me look at the onboarding" asked for. */}
              {!preview && (
                <button className="btn ghost sm" type="button" onClick={signOut}>
                  Sign out
                </button>
              )}
              <div className="spacer" />
              <button className="btn primary" type="submit">
                Next
              </button>
            </div>
          </form>
        )}

        {step === 'partner' && (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault()
              next()
            }}
          >
            <div>
              <h1>Planning with someone?</h1>
              <p className="sub">
                They get an email with a link. Skip this if it is just you — you can invite them later
                from Settings.
              </p>
            </div>
            <div className="field">
              <label htmlFor="ob-pname">Their first name</label>
              <input
                id="ob-pname"
                type="text"
                value={partnerName}
                onChange={(e) => setPartnerName(e.target.value)}
                maxLength={40}
                autoFocus
              />
            </div>
            <div className="field">
              <label htmlFor="ob-pemail">Their email</label>
              <input
                id="ob-pemail"
                type="email"
                value={partnerEmail}
                onChange={(e) => setPartnerEmail(e.target.value)}
                inputMode="email"
              />
            </div>
            <div className="row">
              <button className="btn ghost sm" type="button" onClick={() => setStep('family')}>
                Back
              </button>
              <div className="spacer" />
              {!partnerName.trim() && !partnerEmail.trim() ? (
                <button className="btn primary" type="submit">
                  Just me for now
                </button>
              ) : (
                <button className="btn primary" type="submit">
                  Next
                </button>
              )}
            </div>
          </form>
        )}

        {step === 'allergens' && (
          <div className="stack">
            <div>
              <h1>Anything {someone} can’t eat?</h1>
              <p className="sub">
                Skip it if not. You can add this later from Settings, and change it whenever the
                answer changes.
              </p>
            </div>
            {/*
              One step however many children there are, with a tab each. A step
              per child would put four screens between a family of four and
              their board, and the answer for most of them is "nothing".
            */}
            {named.length > 1 && (
              <div className="kid-tabs" role="tablist" aria-label="Children">
                {named.map((k, i) => {
                  const n = k.allergens.length
                  return (
                    <button
                      key={k.key}
                      role="tab"
                      type="button"
                      aria-selected={i === whichKid}
                      className={`kid-tab ${i === whichKid ? 'on' : ''}`}
                      onClick={() => setWhichKid(i)}
                    >
                      {k.name.trim()}
                      {n > 0 && <span className="kid-tab-count">{n}</span>}
                    </button>
                  )
                })}
              </div>
            )}
            {/*
              Siblings often share a school and its rules, and sometimes a
              diet. Typing the same three answers four times is the part that
              makes a parent give up and do it later, which means never.
            */}
            {named.length > 1 && (
              <div className="row wrap kid-copy">
                {named.map((from, i) =>
                  i === whichKid || from.allergens.length === 0 ? null : (
                    <button
                      key={from.key}
                      className="btn ghost sm"
                      type="button"
                      onClick={() =>
                        setKids((ks) =>
                          ks.map((x) =>
                            x.key === named[whichKid]!.key
                              ? { ...x, allergens: from.allergens.map((a) => ({ ...a })) }
                              : x,
                          ),
                        )
                      }
                    >
                      Same as {from.name.trim()}
                    </button>
                  ),
                )}
              </div>
            )}
            {named[whichKid] && (
              <AllergenPicker
                key={named[whichKid]!.key}
                childName={named[whichKid]!.name}
                value={named[whichKid]!.allergens}
                onChange={(next) =>
                  setKids((ks) => ks.map((x) => (x.key === named[whichKid]!.key ? { ...x, allergens: next } : x)))
                }
              />
            )}
            <div className="row">
              <button className="btn ghost sm" type="button" onClick={() => setStep('partner')}>
                Back
              </button>
              <div className="spacer" />
              <button className="btn primary" type="button" onClick={next}>
                {named.every((k) => k.allergens.length === 0) ? 'Nothing to add' : 'Next'}
              </button>
            </div>
          </div>
        )}

        {step === 'pantry' && (
          <div className="stack">
            <div>
              <h1>{possessive} pantry</h1>
              <p className="sub">
                A starter list of foods most kids eat, shared by everyone in the family. Untick
                anything {someone} won’t, add what’s missing. It is easy to change later, so don’t
                agonise.
              </p>
            </div>
            <div className="starter-paste">
              {!pasting ? (
                <button className="btn sm" type="button" onClick={() => setPasting(true)}>
                  📋 Paste a list instead
                </button>
              ) : (
                <>
                  <div className="field">
                    <label htmlFor="ob-paste">
                      Paste the foods {someone} {kidNames.length === 1 ? 'eats' : 'eat'}, one per line
                    </label>
                    <textarea
                      id="ob-paste"
                      rows={5}
                      value={paste}
                      placeholder={'Ham sandwich\nBlueberries\nCheese cubes\nPretzels'}
                      onChange={(e) => setPaste(e.target.value)}
                    />
                  </div>
                  <div className="row wrap">
                    <button className="btn sm primary" type="button" disabled={!paste.trim()} onClick={addPasted}>
                      Check the list
                    </button>
                    <button
                      className="btn sm ghost"
                      type="button"
                      onClick={() => {
                        setPasting(false)
                        setPaste('')
                        setReview([])
                        setPasted(null)
                      }}
                    >
                      Done pasting
                    </button>
                  </div>
                  <p className="muted small">
                    Anything already on the list below gets ticked back on. Whatever is new comes
                    back here to file.
                  </p>

                  {pasted && (
                    <p className="muted small">
                      {pasted.matched > 0
                        ? `Ticked ${pasted.matched} already on the list. `
                        : 'Nothing matched what is already here. '}
                      {pasted.added > 0
                        ? `${pasted.added} to file below.`
                        : 'Nothing new to file.'}
                    </p>
                  )}

                  {review.length > 0 && (
                    <div className="paste-review">
                      {review.map((r, i) => (
                        <div className="paste-row" key={r.key}>
                          <input
                            type="text"
                            value={r.name}
                            aria-label={`Name of food ${i + 1}`}
                            maxLength={60}
                            onChange={(e) =>
                              setReview((rs) => rs.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x)))
                            }
                          />
                          <select
                            value={r.categoryId}
                            aria-label={`Category for ${r.name || 'this food'}`}
                            onChange={(e) =>
                              setReview((rs) =>
                                rs.map((x) => (x.key === r.key ? { ...x, categoryId: e.target.value } : x)),
                              )
                            }
                          >
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.emoji} {c.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="x"
                            aria-label={`Drop ${r.name || 'this food'}`}
                            onClick={() => setReview((rs) => rs.filter((x) => x.key !== r.key))}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      <div className="row">
                        <button className="btn sm primary" type="button" onClick={keepReviewed}>
                          Add {review.length} {review.length === 1 ? 'food' : 'foods'}
                        </button>
                        <button className="btn sm ghost" type="button" onClick={() => setReview([])}>
                          Drop them
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {droppedCount > 0 && (
              <div className="banner info">
                {droppedCount} {droppedCount === 1 ? 'food is' : 'foods are'} unticked because of what you
                told us. These are the usual recipes, not a reading of any label, so tick one back if
                your version is fine.
              </div>
            )}
            <div className="starter">
              {categories.map((c) => (
                <section key={c.id} className="starter-cat" aria-label={c.name}>
                  <h2>
                    <span aria-hidden="true">{c.emoji}</span> {c.name}
                    <span className="muted"> · {c.blurb}</span>
                  </h2>
                  <ul>
                    {rows
                      .filter((r) => r.categoryId === c.id)
                      .map((r) => (
                        <li key={r.key}>
                          <label className={`starter-row ${r.kept ? '' : 'off'}`}>
                            <input
                              type="checkbox"
                              checked={r.kept}
                              onChange={(e) =>
                                setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, kept: e.target.checked } : x)))
                              }
                            />
                            <Thumb emoji={r.emoji} name={r.name} size="sm" />
                            <span className="name">
                              {r.name}
                              {r.notes && <span className="starter-note">{r.notes}</span>}
                              {hardHits(r).length > 0 ? (
                                <span className="starter-flag severe">
                                  usually has {hardHits(r).map((x) => allergenName({ slug: x, label: '' })).join(', ').toLowerCase()}
                                </span>
                              ) : softHits(r).length > 0 ? (
                                <span className="starter-flag">
                                  usually has {softHits(r).map((x) => allergenName({ slug: x, label: '' })).join(', ').toLowerCase()}
                                </span>
                              ) : null}
                            </span>
                          </label>
                        </li>
                      ))}
                  </ul>
                  <form
                    className="starter-add"
                    onSubmit={(e) => {
                      e.preventDefault()
                      addFood(c.id)
                    }}
                  >
                    <input
                      type="text"
                      value={adding[c.id] ?? ''}
                      onChange={(e) => setAdding((a) => ({ ...a, [c.id]: e.target.value }))}
                      placeholder={`Add a ${c.name.toLowerCase()}…`}
                      aria-label={`Add to ${c.name}`}
                      maxLength={60}
                    />
                    <button className="btn sm" type="submit" disabled={!(adding[c.id] ?? '').trim()}>
                      Add
                    </button>
                  </form>
                </section>
              ))}
            </div>
            <div className="row">
              <button className="btn ghost sm" type="button" onClick={() => setStep('allergens')}>
                Back
              </button>
              <span className="muted" style={{ fontSize: 13 }}>
                {kept} food{kept === 1 ? '' : 's'} kept
              </span>
              <div className="spacer" />
              <button className="btn primary" type="button" onClick={next}>
                Next
              </button>
            </div>
          </div>
        )}

        {step === 'reminder' && (
          <div className="stack">
            <div>
              <h1>A nudge when next week is empty</h1>
              <p className="sub">
                One email, only if nothing is planned yet. Sunday morning is when most families do this.
              </p>
            </div>
            <label className="starter-row">
              <input
                type="checkbox"
                checked={reminder.enabled}
                onChange={(e) => setReminder((r) => ({ ...r, enabled: e.target.checked }))}
              />
              <span className="name">Send the weekly reminder</span>
            </label>
            <div className="row wrap">
              <div className="field">
                <label htmlFor="ob-weekday">Day</label>
                <select
                  id="ob-weekday"
                  value={reminder.weekday}
                  disabled={!reminder.enabled}
                  onChange={(e) => setReminder((r) => ({ ...r, weekday: Number(e.target.value) }))}
                >
                  {WEEKDAYS.map((d, i) => (
                    <option key={d} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="ob-hour">Time</label>
                <select
                  id="ob-hour"
                  value={reminder.hour}
                  disabled={!reminder.enabled}
                  onChange={(e) => setReminder((r) => ({ ...r, hour: Number(e.target.value) }))}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {hourLabel(h)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="ob-zone">Timezone</label>
                <select
                  id="ob-zone"
                  value={reminder.timezone}
                  disabled={!reminder.enabled}
                  onChange={(e) => setReminder((r) => ({ ...r, timezone: e.target.value }))}
                >
                  {zones.map((z) => (
                    <option key={z} value={z}>
                      {z.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row">
              <button className="btn ghost sm" type="button" onClick={() => setStep('pantry')}>
                Back
              </button>
              <div className="spacer" />
              <button className="btn primary" type="button" onClick={create} disabled={busy}>
                {busy ? 'Setting up…' : 'Create my family'}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div className="stack">
            <div>
              <h1>{familyName.trim()} is set up</h1>
              <p className="sub">
                {kept} foods in the pantry{kidNames.length > 1 ? `, shared by ${theirs}` : ''}
                {result.invited ? `, and an invitation is on its way to ${partnerName.trim()}` : ''}.
              </p>
            </div>
            <div>
              <h2 className="h-sm">Put it on your home screen</h2>
              {pwa.canInstall ? (
                <p className="muted" style={{ fontSize: 14 }}>
                  It works like an app: full screen, opens in a tap, and the last week you looked at is
                  there without a signal.{' '}
                  <button className="linklike" type="button" onClick={pwa.install}>
                    Install it
                  </button>
                  .
                </p>
              ) : isIOS ? (
                <p className="muted" style={{ fontSize: 14 }}>
                  It works like an app: full screen, opens in a tap, and the shopping list is there
                  without a signal.{' '}
                  <button className="linklike" type="button" onClick={() => setShowingInstall(true)}>
                    Show me how
                  </button>
                  .
                </p>
              ) : (
                <p className="muted" style={{ fontSize: 14 }}>
                  In your browser’s menu, look for <b>Install app</b> or <b>Add to Home Screen</b>. It
                  opens full screen, like an app, and keeps you signed in.
                </p>
              )}
            </div>
            <button
              className="btn primary"
              type="button"
              onClick={() => (preview ? (window.location.href = '/') : onDone(result.session))}
            >
              Open the board
            </button>
          </div>
        )}
      </div>

      {/* The same steps the app shows after a first planned week, so there is
          one copy of them. Reading them here is not the ask, so it does not
          use up the one prompt that comes later. */}
      {showingInstall && (
        <InstallPrompt
          pwa={pwa}
          profileName={kidNames[0] ?? 'your child'}
          reason="plain"
          silenceOnClose={false}
          onClose={() => setShowingInstall(false)}
        />
      )}
    </div>
  )
}
